/**
 * Plain-English descriptions for the OAuth scopes a provider requests.
 *
 * The catalog hands us raw scope identifiers (`https://www.googleapis.com/
 * auth/calendar.readonly`, `channels:read`). Those are not consent-screen
 * copy, so this maps the ones we ship to a sentence a person can actually
 * weigh before granting access.
 *
 * Deliberately conservative: an unmapped scope is DROPPED rather than
 * paraphrased or shown raw. An inaccurate permissions disclosure is worse than
 * a shorter one, and a scope nobody has written copy for is exactly the case
 * where a guess would be wrong. `describeScopes` reports whether anything was
 * dropped so the UI can say so honestly instead of implying the list is
 * complete.
 */

/** Matched by suffix so provider-prefixed variants resolve to the same copy. */
const SCOPE_COPY: ReadonlyArray<readonly [pattern: string, copy: string]> = [
  // Google Calendar
  ['calendar.readonly', 'Read your calendars and events'],
  ['calendar.events', 'Create and update events on your calendars'],
  ['calendar', 'Read and manage your calendars and events'],

  // Gmail
  ['gmail.readonly', 'Read your email messages and settings'],
  ['gmail.send', 'Send email on your behalf'],
  ['gmail.compose', 'Draft email on your behalf'],
  ['gmail.modify', 'Read, organise and send your email'],

  // Google Drive
  ['drive.readonly', 'Read the files in your Drive'],
  ['drive.file', 'Access only the Drive files you open or create here'],
  ['drive.metadata.readonly', 'Read the names and details of your Drive files'],
  ['drive', 'Read and manage the files in your Drive'],

  // Shared Google identity scopes
  ['userinfo.email', 'See your email address'],
  ['userinfo.profile', 'See your basic profile information'],
  ['openid', 'Confirm who you are with the provider'],

  // Slack
  ['channels:history', 'Read messages in public channels you can access'],
  ['channels:read', 'See the public channels in your workspace'],
  ['groups:read', 'See the private channels you belong to'],
  ['search:read', 'Search the messages you can already see'],
  ['users:read.email', 'Look up workspace members by email address'],
  ['users:read', 'See the members of your workspace'],
  ['chat:write', 'Send messages on your behalf'],
  ['im:history', 'Read your direct messages'],

  // Atlassian (Jira / Confluence)
  ['read:jira-work', 'Read your Jira issues and projects'],
  ['write:jira-work', 'Create and update Jira issues on your behalf'],
  ['read:jira-user', 'See Jira user profiles'],
  ['read:confluence-content.all', 'Read the Confluence pages you can access'],
  ['read:confluence-space.summary', 'See the Confluence spaces you can access'],
  ['write:confluence-content', 'Create and update Confluence pages'],
  ['offline_access', 'Stay connected without asking you to sign in again'],

  // Zoom
  ['meeting:read', 'See your meetings and their details'],
  ['meeting:write', 'Schedule and update meetings on your behalf'],
  ['recording:read', 'Access your meeting recordings and transcripts'],
  ['user:read', 'See your account profile'],
];

export interface ScopeDescription {
  /** Sentences safe to show on a consent screen, deduplicated. */
  readonly described: string[];
  /** How many requested scopes had no vetted copy and were left out. */
  readonly undescribedCount: number;
}

function copyForScope(scope: string): string | undefined {
  const normalized = scope.trim().toLowerCase();
  if (!normalized) return undefined;
  // Longest pattern first so `calendar.readonly` wins over `calendar`.
  const match = [...SCOPE_COPY]
    .sort((a, b) => b[0].length - a[0].length)
    .find(([pattern]) => normalized.endsWith(pattern.toLowerCase()));
  return match?.[1];
}

/**
 * Turns raw scope identifiers into consent-screen copy, dropping any scope we
 * have no vetted wording for and reporting how many were dropped.
 */
export function describeScopes(scopes: readonly string[]): ScopeDescription {
  const described: string[] = [];
  let undescribedCount = 0;

  for (const scope of scopes) {
    const copy = copyForScope(scope);
    if (!copy) {
      undescribedCount += 1;
      continue;
    }
    if (!described.includes(copy)) {
      described.push(copy);
    }
  }

  return { described, undescribedCount };
}
